import { formatDisplayName } from "@/utils/name";
import { reportError } from "@/utils/reportError";
import { supabase } from '@/utils/supabase';
import { useCallback, useEffect, useRef, useState } from 'react';

export interface RankingUser {
  id: string;
  name: string;
  points: number;
  rank?: number;
  medal?: 'gold' | 'silver' | 'bronze';
  avatarUrl?: string;
}

export interface MyRanking {
  userId: string;
  totalPoints: number;
  weeklyPoints: number;
  globalRank: number | null;
  companyRank: number | null;
  companyName: string | null;
}

export function useRanking() {
  const [globalRanking, setGlobalRanking] = useState<RankingUser[]>([]);
  const [companyRanking, setCompanyRanking] = useState<RankingUser[]>([]);
  const [myRanking, setMyRanking] = useState<MyRanking | null>(null);
  const [userCompanyId, setUserCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Le spinner plein écran ne doit s'afficher qu'au 1er chargement : l'écran
  // Classement refetch à chaque focus (useFocusEffect), sinon toute la liste
  // clignotait/scroll perdu à chaque retour sur l'onglet.
  const hasLoadedRef = useRef(false);

  const fetchRankings = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setLoading(false);
        return;
      }

      // Get current user's profile to check company
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      // Une erreur ici ne doit pas faire croire « pas d'entreprise » (écran
      // « Rejoins une entreprise » affiché à tort) : on remonte au catch, qui
      // reporte et conserve l'état précédent.
      if (profileError) throw profileError;

      setUserCompanyId(profile?.company_id || null);

      // Fetch my ranking
      const { data: myRankingData } = await supabase
        .rpc('get_my_ranking');

      if (myRankingData && myRankingData.length > 0) {
        const data = myRankingData[0];
        setMyRanking({
          userId: data.user_id,
          totalPoints: data.total_points || 0,
          weeklyPoints: data.weekly_points || 0,
          globalRank: data.global_rank,
          companyRank: data.company_rank,
          companyName: data.company_name,
        });
      }

      // Fetch global ranking using RPC function
      const { data: globalData, error: globalError } = await supabase
        .rpc('get_ranking_global', { limit_count: 50 });

      if (globalError) throw globalError;

      if (globalData) {
        const rankings: RankingUser[] = globalData.map((item: any, index: number) => ({
          id: item.user_id,
          name: formatDisplayName(item.first_name, item.last_name) || 'Utilisateur',
          points: item.total_points || 0,
          rank: index + 1,
          avatarUrl: item.avatar_url,
        }));

        // Assign medals to top 3
        if (rankings.length >= 1) rankings[0].medal = 'gold';
        if (rankings.length >= 2) rankings[1].medal = 'silver';
        if (rankings.length >= 3) rankings[2].medal = 'bronze';

        setGlobalRanking(rankings);
      }

      // Fetch company ranking if user has a company
      if (profile?.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .rpc('get_ranking_by_company', { 
            target_company_id: profile.company_id,
            limit_count: 50 
          });

        if (companyError) throw companyError;

        if (companyData) {
          const rankings: RankingUser[] = companyData.map((item: any, index: number) => ({
            id: item.user_id,
            name: formatDisplayName(item.first_name, item.last_name) || 'Utilisateur',
            points: item.total_points || 0,
            rank: index + 1,
            avatarUrl: item.avatar_url,
          }));

          // Assign medals to top 3
          if (rankings.length >= 1) rankings[0].medal = 'gold';
          if (rankings.length >= 2) rankings[1].medal = 'silver';
          if (rankings.length >= 3) rankings[2].medal = 'bronze';

          setCompanyRanking(rankings);
        }
      }
    } catch (err: any) {
      reportError(err);
      setError(err.message);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  const hasCompany = userCompanyId !== null;

  return {
    globalRanking,
    companyRanking,
    myRanking,
    hasCompany,
    loading,
    error,
    refetch: fetchRankings,
  };
}

